import axiosInstance from '../helpers/axios';

/**
 * /api/ops/scheduling — the reservation lifecycle without GoMeddo: the
 * stage on a class (Hold with expiry → SME Requested → SME Confirmed →
 * Fully Confirmed), non-teaching blocks on the shared calendar, the
 * resource registry, the drift monitor until cutover, the free-slot finder.
 */

export type TReservationSettings = {
  source: 'mirror' | 'portal';
  holdDays: number;
  zoomRoomBufferMinutes: number;
  workingDay: { startUtcHour: number; endUtcHour: number };
  statuses: readonly string[];
  settable: readonly string[];
  blockKinds: readonly string[];
};

export type TReservationStage = {
  classId: string;
  status: string | null;
  basis: 'portal' | 'reservation' | 'none';
  mirroredStatus: string | null;
  reservationId: string | null;
  holdExpiresAt: string | null;
  holdExpired: boolean;
  note: string | null;
  setAt: string | null;
  setBy: string | null;
  clientConfirmed: boolean;
  settable: string[];
  history: {
    at: string;
    by: string;
    from: string | null;
    to: string | null;
    note: string | null;
  }[];
};

export type TCalendarBlock = {
  id: string;
  kind: string;
  instructorId: string | null;
  instructorName: string | null;
  from: string;
  to: string;
  note: string | null;
  createdBy: string | null;
  createdAt: string | null;
  classesInside?: { id: string; name: string | null; start: string | null }[];
};

export type TDriftItem = {
  id: string;
  name: string | null;
  classNumber: string | null;
  start: string | null;
  instructor: string | null;
  status: string | null;
  accountName: string | null;
  reservationId: string | null;
  portalEdited: boolean;
  portalEditedFields: string[];
  staleSide: 'reservation' | 'unknown';
  drift: { field: string; portal: string | null; reservation: string | null }[];
};

export type TDrift = {
  generatedAt: string;
  source: 'mirror' | 'portal';
  skipped: boolean;
  reason?: string;
  days?: number;
  checked?: number;
  count: number;
  byField?: Record<string, number>;
  items: TDriftItem[];
};

export type TFreeSlots = {
  instructor: string;
  matchedIdentity: boolean;
  from: string;
  to: string;
  hours: number;
  weekdays: number[];
  busyCount: number;
  windows: { start: string; end: string }[];
};

export const getReservationSettings = async () =>
  (await axiosInstance.get<TReservationSettings>('ops/scheduling/settings'))
    .data;

export const getReservationStage = async (classId: string) =>
  (
    await axiosInstance.get<TReservationStage>(
      `ops/scheduling/classes/${classId}/reservation`,
    )
  ).data;

export const setReservationStage = async (
  classId: string,
  body: { status: string; holdDays?: number; note?: string | null },
) =>
  (
    await axiosInstance.post<{
      classId: string;
      from: string | null;
      status: string;
      holdExpiresAt: string | null;
      clientConfirmed: boolean;
    }>(`ops/scheduling/classes/${classId}/reservation`, body)
  ).data;

export const getCalendarBlocks = async (from: string, to: string) =>
  (
    await axiosInstance.get<{ items: TCalendarBlock[] }>(
      'ops/scheduling/blocks',
      { params: { from, to } },
    )
  ).data.items;

export const addCalendarBlock = async (body: {
  kind: string;
  instructorId?: string | null;
  from: string;
  to?: string | null;
  note?: string | null;
}) =>
  (await axiosInstance.post<TCalendarBlock>('ops/scheduling/blocks', body))
    .data;

export const removeCalendarBlock = async (id: string) =>
  (
    await axiosInstance.delete<{ id: string; removed: boolean }>(
      `ops/scheduling/blocks/${id}`,
    )
  ).data;

export const getReservationDrift = async (days?: number) =>
  (
    await axiosInstance.get<TDrift>('ops/scheduling/drift', {
      params: { days },
    })
  ).data;

export const getFreeSlots = async (params: {
  instructor: string;
  from: string;
  to: string;
  hours: number;
  weekdays?: number[];
}) =>
  (
    await axiosInstance.get<TFreeSlots>('ops/scheduling/free-slots', {
      params: { ...params, weekdays: params.weekdays?.join(',') },
    })
  ).data;

export const getOpsInstructorIdentities = async () =>
  (
    await axiosInstance.get<{
      items: { id: string; displayName: string; variants: string[] }[];
    }>('ops/instructors')
  ).data.items;

export type TRunBoardItem = {
  id: string;
  name: string | null;
  classNumber: string | null;
  classType: string | null;
  status: string | null;
  reservationStatus: string | null;
  start: string | null;
  end: string | null;
  timeZone: string | null;
  accountId: string | null;
  accountName: string | null;
  instructor: string | null;
  room: string | null;
  joinUrl: string | null;
  location: string | null;
  locationAddress: string | null;
  videoProvider: string | null;
  registered: number;
  seats: number | null;
  prepCallStatus: string | null;
  localOrigin: boolean;
  flags: string[];
};

export type TRunBoard = {
  generatedAt: string;
  days: number;
  total: number;
  flagged: number;
  buckets: {
    key: string;
    label: string;
    from: string;
    to: string;
    items: TRunBoardItem[];
  }[];
};

/** The morning run board: today, tomorrow, this week, next two weeks. */
export const getRunBoard = async (days = 14) =>
  (
    await axiosInstance.get<TRunBoard>('ops/scheduling/run-board', {
      params: { days },
    })
  ).data;
